import type { ApiResponse } from './ApiTypes';
import { notify } from 'notiwind'
/**
 * API Service for making HTTP requests
 */
export class ApiService {
  private baseUrl: string;
  private headers: Record<string, string>;

  /**
   * Creates a new ApiService instance
   * @param baseUrl The base URL for API requests
   * @param token Optional JWT token for authentication
   */
  constructor(baseUrl: string, token?: string) {
    this.baseUrl = baseUrl;
    this.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  /**
   * Updates the authentication token
   * @param token The JWT token for authentication
   */
  setToken(token?: string): void {
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * Makes a GET request
   * @param endpoint The API endpoint
   * @param params Query parameters
   * @returns Promise with the API response
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseUrl}${endpoint}`);
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.headers
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  /**
   * Makes a POST request
   * @param endpoint The API endpoint
   * @param data The data to send
   * @returns Promise with the API response
   */
  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {

      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(data)
      });

      

      return this.handleResponse<T>(response);
    } catch (error) {
      
      return this.handleError<T>(error);
    }
  }

  /**
   * Makes a PUT request
   * @param endpoint The API endpoint
   * @param data The data to send
   * @returns Promise with the API response
   */
  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(data)
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  /**
   * Makes a DELETE request
   * @param endpoint The API endpoint
   * @returns Promise with the API response
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.headers
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return this.handleError<T>(error);
    }
  }

  /**
   * Handles the API response
   * @param response The fetch response
   * @returns The parsed API response
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let data: any;
    
    try {
      // Check if response has content
      const text = await response.text();
      if (!text) {
        return {
          success: false,
          message: 'Empty response from server',
          errorCode: response.status,
          data: null
        };
      }
      
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      return {
        success: false,
        message: 'Invalid JSON response from server',
        errorCode: response.status,
        data: null
      };
    }
    
    if (!response.ok) {
      // If unauthorized (401), redirect to login (lazy import router to avoid circular deps)
      if (response.status === 401) {
        localStorage.removeItem('auth_token_for_mani');
        try {
          const { default: router } = await import('../router');
          router.push('/login');
        } catch (e) {
          console.error('Failed to navigate to login after 401:', e);
        }
      }
      
      // If forbidden (403), show a notification with the error message
      if (response.status === 403) {
        console.log("403 Error Response Data:", data);
        const errorMessage = data.message || 
                             data.error || 
                             (data.body ? JSON.stringify(data.body) : JSON.stringify(data));
        notify({
          group: 'foo',
          title: 'Erişim Reddedildi',
          type: 'warning',
          text: errorMessage,
        }, 4000); // 4s
      }
      
      return {
        success: false,
        message: data.message || 'Bir hata oluştu',
        errorCode: data.errorCode || response.status,
        data: data.data ?? null
      };
    }

    // If the API already returns in our format
    if (data.hasOwnProperty('success') && data.hasOwnProperty('data')) {
      return data as ApiResponse<T>;
    }

    // Otherwise, format the response
    return {
      success: true,
      message: null,
      errorCode: null,
      data: data as T
    };
  }

  /**
   * Handles errors from fetch
   * @param error The error object
   * @returns An error API response
   */
  private handleError<T>(error: any): ApiResponse<T> {

    notify(
      {
        group: 'foo',
        title: 'Hata',
        type: 'warning',
        text: error.message || 'Bir hata oluştu',
      },
      4000,
    ) // 4s

    return {
      success: false,
      message: error.message || 'Ağ hatası',
      errorCode: 0,
      data: null
    };
  }
}

// Default export da ekleyelim
export default ApiService; 